import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./SubsWall.css";

// ============================================================================
// Domain types
// ============================================================================

export type Theme = "graffiti" | "atelier" | "glow" | "neon" | "chalkboard" | "stickies";

export type TileStyle =
  // Graffiti theme styles
  | "spray" | "neon" | "marker" | "chalk" | "sticker" | "carved"
  // Atelier theme styles (YouTube-pro audience)
  | "card" | "inked" | "signature" | "gilded" | "engraved";

export type TierKey = "tagger" | "painter" | "hooligan" | "crewboss" | "sovereign" | "legend";

export type Badge = "crown" | "fire" | "lightning" | "star" | "shield" | "skull";

export interface SubTile {
  id: string;
  name: string;
  message: string; // ≤160 chars
  style: TileStyle;
  color: string;
  xPct: number; // 0..100
  yPct: number; // 0..100
  monthlyCents: number;
  blink?: boolean;
  badge?: Badge | null;
  founderNumber?: number | null;
  anniversaryMonths?: number;
  daysSubscribed?: number; // optional explicit override; falls back to anniversaryMonths × 30
  boostUntil?: number | null;
  fadeStartedAt?: number | null;
  creatorReply?: string | null;        // creator's response to this supporter
  creatorReplyAt?: number | null;
  emoji?: string | null;               // one unicode emoji, displayed next to the name
}

/**
 * Days a supporter has been on the wall. If not set explicitly, derive from
 * anniversaryMonths × 30. Floor at 1 day so brand-new supporters show "Day 1".
 */
export function daysForTile(tile: SubTile): number {
  if (typeof tile.daysSubscribed === "number") return Math.max(1, Math.floor(tile.daysSubscribed));
  const m = tile.anniversaryMonths ?? 0;
  return Math.max(1, Math.floor(m * 30 + 7));
}

// ============================================================================
// Tiers — same money tiers across themes; only labels + effects differ.
// ============================================================================

export interface TierDef {
  key: TierKey;
  min: number;
  graffitiLabel: string;
  atelierLabel: string;
  /** Concrete "buys you this" gift — escalating lifestyle ladder. */
  giftIcon: string;
  giftLabel: string;
}

export const TIERS: TierDef[] = [
  { key: "tagger",    min: 500,   graffitiLabel: "TAGGER",    atelierLabel: "SUPPORTER",  giftIcon: "☕",  giftLabel: "a coffee" },
  { key: "painter",   min: 1000,  graffitiLabel: "PAINTER",   atelierLabel: "MEMBER",     giftIcon: "🍕",  giftLabel: "a pizza" },
  { key: "hooligan",  min: 2500,  graffitiLabel: "HOOLIGAN",  atelierLabel: "PATRON",     giftIcon: "🍣",  giftLabel: "sushi night" },
  { key: "crewboss",  min: 5000,  graffitiLabel: "CREWBOSS",  atelierLabel: "BENEFACTOR", giftIcon: "🍷",  giftLabel: "a bottle of wine" },
  { key: "sovereign", min: 10000, graffitiLabel: "SOVEREIGN", atelierLabel: "FOUNDER",    giftIcon: "🍽️", giftLabel: "a tasting menu" },
  { key: "legend",    min: 25000, graffitiLabel: "LEGEND",    atelierLabel: "ARCHITECT",  giftIcon: "✈️",  giftLabel: "a weekend trip" },
];

export function tierGift(cents: number): { icon: string; label: string } {
  const def = TIERS.find((x) => x.key === tierFor(cents)) ?? TIERS[0];
  return { icon: def.giftIcon, label: def.giftLabel };
}

export function tierLabel(key: TierKey, theme: Theme): string {
  const t = TIERS.find((x) => x.key === key);
  if (!t) return key;
  return theme === "atelier" ? t.atelierLabel : t.graffitiLabel;
}

export function tierFor(cents: number): TierKey {
  let t: TierKey = "tagger";
  for (const x of TIERS) if (cents >= x.min) t = x.key;
  return t;
}

export function tierAtLeast(cents: number, key: TierKey): boolean {
  const order: TierKey[] = ["tagger", "painter", "hooligan", "crewboss", "sovereign", "legend"];
  return order.indexOf(tierFor(cents)) >= order.indexOf(key);
}

// ============================================================================
// Theme-aware unlocks
// ============================================================================

export interface TierUnlocks {
  styles: TileStyle[];
  canBlink: boolean;       // graffiti only
  canSway: boolean;        // graffiti only
  canBadge: boolean;       // both (badges in atelier = monogram instead of emoji)
  alwaysOnTop: boolean;
  hasAura: boolean;        // graffiti → glow halo; atelier → hover lift
  isRainbow: boolean;      // graffiti only; atelier uses gold foil at this tier
  isGilded: boolean;       // atelier counterpart to rainbow — gold foil shimmer
  entryAnim: boolean;
  isFounder: boolean;
  sizeCapMultiplier: number;
}

const GRAFFITI_STYLES_BY_TIER: Record<TierKey, TileStyle[]> = {
  tagger:    ["spray", "marker", "chalk"],
  painter:   ["spray", "marker", "chalk", "neon"],
  hooligan:  ["spray", "marker", "chalk", "neon", "sticker"],
  crewboss:  ["spray", "neon", "marker", "chalk", "sticker", "carved"],
  sovereign: ["spray", "neon", "marker", "chalk", "sticker", "carved"],
  legend:    ["spray", "neon", "marker", "chalk", "sticker", "carved"],
};

const ATELIER_STYLES_BY_TIER: Record<TierKey, TileStyle[]> = {
  tagger:    ["card"],
  painter:   ["card", "inked"],
  hooligan:  ["card", "inked", "signature"],
  crewboss:  ["card", "inked", "signature", "gilded"],
  sovereign: ["card", "inked", "signature", "gilded", "engraved"],
  legend:    ["card", "inked", "signature", "gilded", "engraved"],
};

export function unlocksFor(cents: number, theme: Theme = "graffiti"): TierUnlocks {
  const t = tierFor(cents);
  const isGraffiti = theme === "graffiti";
  const isAtelier = theme === "atelier";
  return {
    styles: isAtelier ? ATELIER_STYLES_BY_TIER[t] : GRAFFITI_STYLES_BY_TIER[t],
    canBlink:    isGraffiti && tierAtLeast(cents, "painter"),
    canSway:     isGraffiti && tierAtLeast(cents, "hooligan"),
    canBadge:    tierAtLeast(cents, "crewboss"),
    alwaysOnTop: tierAtLeast(cents, "crewboss"),
    hasAura:     tierAtLeast(cents, "crewboss"),
    isRainbow:   isGraffiti && tierAtLeast(cents, "sovereign"),
    isGilded:    isAtelier && tierAtLeast(cents, "sovereign"),
    entryAnim:   tierAtLeast(cents, "sovereign"),
    isFounder:   tierAtLeast(cents, "legend"),
    sizeCapMultiplier: tierAtLeast(cents, "sovereign") ? 1.5 : 1.0,
  };
}

// ============================================================================
// Palettes (theme-scoped)
// ============================================================================

export const STYLE_PALETTES: Record<TileStyle, string[]> = {
  // Graffiti — saturated, electric
  spray:   ["#ff2d55", "#ff9500", "#ffcc00", "#34c759", "#5ac8fa", "#007aff", "#af52de", "#ff3b30"],
  neon:    ["#ff00aa", "#00ffe1", "#39ff14", "#ffea00", "#ff6a00", "#7a00ff", "#00b3ff", "#ff2bd6"],
  marker:  ["#111111", "#222831", "#3a3a3a", "#5e2ca5", "#d62828", "#003049", "#264653", "#1a4f3a"],
  chalk:   ["#fce4ec", "#fff3b0", "#cdeac0", "#bde0fe", "#e0c3fc", "#ffd6a5", "#caffbf", "#a0c4ff"],
  sticker: ["#ffadad", "#ffd6a5", "#fdffb6", "#caffbf", "#9bf6ff", "#a0c4ff", "#bdb2ff", "#ffc6ff"],
  carved:  ["#8d6e63", "#6d4c41", "#5d4037", "#a1887f", "#4e342e", "#3e2723", "#795548", "#bcaaa4"],
  // Atelier — muted, editorial
  card:      ["#1a1a1c", "#3d4f5c", "#4a4a4a", "#5b3a3a", "#3a4a3a", "#5a4a32", "#34495e", "#3e2e3e"],
  inked:     ["#1a1a1c", "#324151", "#5a3a3a", "#3e5641", "#7a5a32", "#4a3458", "#26344e", "#2f3e34"],
  signature: ["#1a1a1c", "#2c3e50", "#5d4037", "#3e5641", "#7a5a32", "#5d3e58", "#2c4e5e", "#3d3d4f"],
  gilded:    ["#c9a961", "#b8860b", "#d4af37", "#e6c200", "#a07a2c", "#bf953f", "#8b7218", "#bda14e"],
  engraved:  ["#2c2c2c", "#1c1c1e", "#3a3a3a", "#262626", "#1f1f1f", "#333333", "#2a2a2a", "#202020"],
};

export const STYLE_LABEL: Record<TileStyle, string> = {
  // Graffiti
  spray: "SPRAY", neon: "NEON", marker: "MARKER", chalk: "CHALK", sticker: "STICKER", carved: "CARVED",
  // Atelier
  card: "CARD", inked: "INKED", signature: "SIGNATURE", gilded: "GILDED", engraved: "ENGRAVED",
};

export const BADGE_GLYPH: Record<Badge, string> = {
  crown: "👑", fire: "🔥", lightning: "⚡", star: "⭐", shield: "🛡", skull: "💀",
};

// ============================================================================
// Sizing
// ============================================================================

export function sizeFor(monthlyCents: number, theme: Theme = "graffiti"): number {
  const dollars = Math.max(1, monthlyCents / 100);
  const isFounder = unlocksFor(monthlyCents, theme).sizeCapMultiplier > 1;
  // Reference (5d94b180…): KAIROS $5 ≈ 1.0 unit wide. AURORA $250 ≈ 2.8
  // units wide and dominates the right half of the canvas.
  const raw = (1 + Math.log10(dollars / 5 + 1) * 0.5) * (isFounder ? 1.9 : 1.0);
  const cap = isFounder ? 2.8 : 1.2;
  return Math.min(cap, Math.max(1.0, raw));
}

// ============================================================================
// Component
// ============================================================================

const SNAP_PX = 8;
const FADE_MS = 7 * 24 * 60 * 60 * 1000;
const clamp01 = (n: number): number => Math.min(1, Math.max(0, n));
// Drag/click clamp range — keeps tiles inside the wall even at the largest
// scaled width. 12% margin on each side is enough headroom for a founder
// tile at the 1.5× cap on a narrow mobile canvas.
const SAFE_MIN_PCT = 12;
const SAFE_MAX_PCT = 88;
const clampPct = (n: number): number => Math.min(SAFE_MAX_PCT, Math.max(SAFE_MIN_PCT, n));

// ============================================================================
// Collision-aware placement
// ============================================================================

/** Approximate tile bounding box in canvas-percent units (half-width, half-height).
 *  Calibrated against the neon-theme min-width: 130px, max-width: 220px and
 *  the sizeFor() output. Generous on purpose — we'd rather pad too much than
 *  let tiles touch. Canvas aspect is taken into account so a "fat" wall
 *  doesn't compute tiny heights. */
export function tileHalfExtent(tile: SubTile, _canvasAspect = 1, theme: Theme = "neon"): { hx: number; hy: number } {
  const s = sizeFor(tile.monthlyCents, theme);
  // Calibrated against actual rendered tile sizes (CSS max-width 300px
  // × scale on a ~1100px canvas = realistic at desktop). Mobile uses
  // scaleMult to shrink everything, so these % stay correct there too.
  const baseWPct = 5 + s * 5;        // 10% half for $5, 25% half for $250 founder
  const baseHPct = 6 + s * 4;        // 10% half for $5, 22% half for $250 founder
  return { hx: baseWPct, hy: baseHPct };
}

/** Two tiles overlap (with min padding) if their bboxes intersect on both axes. */
function tilesOverlap(
  ax: number, ay: number, ahx: number, ahy: number,
  bx: number, by: number, bhx: number, bhy: number,
  paddingPct = 7,  // visible breathing room between any two tiles
): boolean {
  return (
    Math.abs(ax - bx) < ahx + bhx + paddingPct &&
    Math.abs(ay - by) < ahy + bhy + paddingPct
  );
}

/** Find a non-overlapping slot for a new tile, starting from (targetX, targetY)
 *  and spiraling outward. Returns the closest empty slot or the targetX/Y if
 *  the canvas is too full (caller can choose what to do). */
export function findNonOverlappingPosition(
  targetX: number,
  targetY: number,
  newTile: { monthlyCents: number; style: TileStyle },
  existingTiles: SubTile[],
  canvasAspect = 1,
  theme: Theme = "neon",
): { x: number; y: number } {
  const fakeTile = { ...existingTiles[0], ...newTile } as SubTile;
  const me = tileHalfExtent(fakeTile, canvasAspect, theme);

  const isFree = (cx: number, cy: number): boolean => {
    for (const t of existingTiles) {
      const ot = tileHalfExtent(t, canvasAspect, theme);
      if (tilesOverlap(cx, cy, me.hx, me.hy, t.xPct, t.yPct, ot.hx, ot.hy)) return false;
    }
    return true;
  };

  const clampedTarget = { x: clampPct(targetX), y: clampPct(targetY) };
  if (isFree(clampedTarget.x, clampedTarget.y)) return clampedTarget;

  // Spiral outward in fine 2% steps — ~1500 candidates explored.
  const STEP = 2;
  for (let ring = 1; ring <= 36; ring++) {
    const r = ring * STEP;
    const trials = Math.max(16, ring * 6);
    for (let i = 0; i < trials; i++) {
      const angle = (i / trials) * Math.PI * 2;
      const cx = clampPct(clampedTarget.x + Math.cos(angle) * r);
      const cy = clampPct(clampedTarget.y + Math.sin(angle) * r);
      if (isFree(cx, cy)) return { x: cx, y: cy };
    }
  }
  // Last resort — pick the position with the maximum min-distance to any
  // existing tile. Better to land "as far from anyone as possible" than
  // visibly overlap.
  let best = clampedTarget;
  let bestMinD = -Infinity;
  for (let cx = SAFE_MIN_PCT; cx <= SAFE_MAX_PCT; cx += 3) {
    for (let cy = SAFE_MIN_PCT; cy <= SAFE_MAX_PCT; cy += 3) {
      let minD = Infinity;
      for (const t of existingTiles) {
        const d = Math.hypot(cx - t.xPct, cy - t.yPct);
        if (d < minD) minD = d;
      }
      if (minD > bestMinD) { bestMinD = minD; best = { x: cx, y: cy }; }
    }
  }
  return best;
}

function fadeOpacityFor(fadeStartedAt: number | null | undefined): number {
  if (!fadeStartedAt) return 1;
  const elapsed = Date.now() - fadeStartedAt;
  if (elapsed <= 0) return 1;
  if (elapsed >= FADE_MS) return 0;
  return 1 - elapsed / FADE_MS;
}

function isBoosted(t: SubTile): boolean {
  return !!t.boostUntil && t.boostUntil > Date.now();
}

/**
 * Track the wall's actual rendered width so we can scale tiles down on
 * narrow viewports. A tier-6 tile at the desktop max (~3.5×) would otherwise
 * exceed the wall's pixel width on mobile and overflow into chaos.
 */
function useWallScaleMultiplier(ref: React.RefObject<HTMLDivElement>): number {
  const [mult, setMult] = useState(1);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const compute = (): void => {
      const w = el.clientWidth;
      // Anchor: at 1100px wide the multiplier is 1.0; at 360px it's ~0.45.
      const m = Math.max(0.42, Math.min(1, w / 1100));
      setMult(m);
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    window.addEventListener("resize", compute);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", compute);
    };
  }, [ref]);
  return mult;
}

export interface SubsWallProps {
  tiles: SubTile[];
  editableTileId?: string | null;
  onTileMove?: (id: string, xPct: number, yPct: number) => void;
  aspectRatio?: number;
  theme?: Theme;
  /** When set, the matching tile gets a dramatic pulsing spotlight ring.
      Used right after a fan signs the wall so they can find their tile. */
  spotlightTileId?: string | null;
  /** When true, ignore aspectRatio and grow the wall taller as tile
      count rises so tiles never need to crowd. Use on public profile +
      embed. The compose flow keeps the fixed aspect for the preview. */
  autoGrow?: boolean;
  /** Creator-uploaded custom background image (R2 URL). When set, the
      theme background is dimmed and this image overlays as wallpaper. */
  bgImageUrl?: string | null;
  /** 0..1 image opacity; default 0.55. Lower = quieter wallpaper. */
  bgImageOpacity?: number | null;
  /** Custom font family applied to tile names + headers. */
  customFont?: string | null;
  /** Custom accent color for spotlight ring + reply tag + UI accents. */
  customAccent?: string | null;
}

/**
 * Compute a canvas aspect that's guaranteed to fit every tile with padding.
 * Sums up rough tile areas (using sizeFor() × calibrated bbox) and derives
 * the height needed assuming ~70% packing efficiency. Falls back to the
 * caller-provided aspect when the wall is sparse enough not to need extra
 * room.
 */
export function aspectForTiles(tiles: SubTile[], fallback = 16 / 9, theme: Theme = "neon"): number {
  if (tiles.length === 0) return fallback;
  let totalArea = 0;
  let maxHy = 0;
  for (const t of tiles) {
    const { hx, hy } = tileHalfExtent(t, 1, theme);
    totalArea += (2 * hx) * (2 * hy);
    if (hy > maxHy) maxHy = hy;
  }
  totalArea *= 1.1;
  totalArea += 30 * 30;
  const neededHeight = totalArea / (0.85 * 100);
  const minHeight = Math.max(neededHeight, maxHy * 2 + 16);
  const computed = 100 / minHeight;
  // Keep walls roughly landscape but always tall enough to fit the
  // largest tile + breathing room. With a founder ($250) in the mix
  // 6 tiles need ~1.4 aspect; pure small tiles can stay at 16:9.
  return Math.min(fallback, Math.max(1.25, computed));
}

// Back-compat alias kept for the embed widget.
export function aspectForTileCount(count: number, fallback = 16 / 9): number {
  if (count <= 5) return fallback;
  const extraBands = Math.ceil((count - 5) / 5);
  return Math.max(0.5, fallback / (1 + extraBands * 0.35));
}

export default function SubsWall(props: SubsWallProps): JSX.Element {
  const {
    tiles, editableTileId = null, onTileMove, aspectRatio = 16 / 9, theme = "graffiti",
    spotlightTileId = null, autoGrow = false,
    bgImageUrl = null, bgImageOpacity = null,
    customFont = null, customAccent = null,
  } = props;
  // autoGrow now uses tile-area math (not just count) so a $250 founder
  // tile always forces enough vertical room.
  const effectiveAspect = autoGrow ? aspectForTiles(tiles, aspectRatio, theme) : aspectRatio;
  const wrapRef = useRef<HTMLDivElement>(null);
  const scaleMult = useWallScaleMultiplier(wrapRef);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [ghostPos, setGhostPos] = useState<{ x: number; y: number } | null>(null);
  // Speech-bubble interaction: clicking a tile opens a popup with the
  // supporter's full message. Only one bubble open at a time. Tap-outside or
  // tap-same-tile closes. Tracked by tile id so re-renders keep the right
  // bubble pinned to the right tile.
  const [bubbleTileId, setBubbleTileId] = useState<string | null>(() => {
    // Debug hook: ?bubble=<tileId> auto-opens that tile's bubble on mount.
    // Used by the design-review screenshot harness. No-op in normal use.
    if (typeof window !== "undefined") {
      const m = window.location.search.match(/[?&]bubble=([^&]+)/);
      if (m) return decodeURIComponent(m[1]);
    }
    return null;
  });
  // Track press position+time so we can distinguish a click (open bubble)
  // from a drag (move tile in editable mode) or a scroll-cancel.
  const pressRef = useRef<{ id: string; x: number; y: number; t: number } | null>(null);

  // Resolve overlaps once for the rendered tile set: place the LARGEST tile
  // at its requested position, then place each smaller tile via the spiral
  // search so it never overlaps an already-placed tile. Protects against
  // hand-tuned mock positions (or stale DB rows) drifting into each other
  // after a size-formula bump.
  const resolved = useMemo(() => {
    const sorted = [...tiles].sort(
      (a, b) => sizeFor(b.monthlyCents, theme) - sizeFor(a.monthlyCents, theme),
    );
    const placed: SubTile[] = [];
    const out = new Map<string, { x: number; y: number }>();
    for (const t of sorted) {
      const pos = findNonOverlappingPosition(t.xPct, t.yPct, t, placed, effectiveAspect, theme);
      out.set(t.id, pos);
      placed.push({ ...t, xPct: pos.x, yPct: pos.y });
    }
    return out;
  }, [tiles, theme, effectiveAspect]);

  const ordered = useMemo(() => {
    const arr = [...tiles];
    // Render order (low → high z): legacy → normal → premium → boosted.
    // Legacy = cancelled-and-fading. Pushed under everything so active
    // supporters never sit behind a desaturated tile.
    const tierOf = (t: SubTile): number => {
      if (t.fadeStartedAt) return 0;
      if (isBoosted(t)) return 4;
      if (unlocksFor(t.monthlyCents, theme).alwaysOnTop) return 3;
      return 1;
    };
    arr.sort((a, b) => {
      const sa = tierOf(a);
      const sb = tierOf(b);
      if (sa !== sb) return sa - sb;
      // Within the same tier: smaller tiles render LAST (highest z) so a $5
      // supporter's name is never completely covered by a $250 founder
      // tile. Every signature must remain visible — that's the product.
      return sizeFor(a.monthlyCents, theme) - sizeFor(b.monthlyCents, theme);
    });
    if (draggingId) {
      const i = arr.findIndex((t) => t.id === draggingId);
      if (i >= 0) {
        const [drag] = arr.splice(i, 1);
        arr.push(drag);
      }
    }
    return arr;
  }, [tiles, draggingId, theme]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent, tile: SubTile) => {
      // Record press for click-vs-drag arbitration on pointerup (used by
      // the speech-bubble open handler).
      pressRef.current = { id: tile.id, x: e.clientX, y: e.clientY, t: Date.now() };
      if (tile.id !== editableTileId) return;
      (e.target as Element).setPointerCapture(e.pointerId);
      setDraggingId(tile.id);
    },
    [editableTileId],
  );

  // Tile pointerup: if the pointer barely moved and we weren't dragging,
  // treat it as a click and toggle the bubble. Same-tile click closes;
  // different-tile click swaps; anywhere-else click closes via the
  // backdrop handler below.
  const onTilePointerUp = useCallback(
    (e: React.PointerEvent, tile: SubTile) => {
      const p = pressRef.current;
      pressRef.current = null;
      if (!p || p.id !== tile.id) return;
      const moved = Math.hypot(e.clientX - p.x, e.clientY - p.y);
      const elapsed = Date.now() - p.t;
      // 6px movement and <500ms == click. Anything more is a drag/scroll.
      if (moved > 6 || elapsed > 500) return;
      if (draggingId) return; // active drag wins over click
      setBubbleTileId((prev) => (prev === tile.id ? null : tile.id));
    },
    [draggingId],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!draggingId || !wrapRef.current) return;
      const rect = wrapRef.current.getBoundingClientRect();
      const xPx = Math.round((e.clientX - rect.left) / SNAP_PX) * SNAP_PX;
      const yPx = Math.round((e.clientY - rect.top) / SNAP_PX) * SNAP_PX;
      setGhostPos({
        x: clampPct(clamp01(xPx / rect.width) * 100),
        y: clampPct(clamp01(yPx / rect.height) * 100),
      });
    },
    [draggingId],
  );

  const onPointerUp = useCallback(() => {
    if (draggingId && ghostPos && onTileMove) {
      // Snap to nearest non-overlapping slot so tiles never stack on each other.
      const me = tiles.find((t) => t.id === draggingId);
      if (me) {
        const others = tiles.filter((t) => t.id !== draggingId);
        const snapped = findNonOverlappingPosition(
          ghostPos.x, ghostPos.y, me, others, aspectRatio, theme,
        );
        onTileMove(draggingId, snapped.x, snapped.y);
      } else {
        onTileMove(draggingId, ghostPos.x, ghostPos.y);
      }
    }
    setDraggingId(null);
    setGhostPos(null);
  }, [draggingId, ghostPos, onTileMove, tiles, aspectRatio, theme]);

  // Tap-to-place: clicking empty canvas (NOT on a tile) moves the editable
  // tile there. Desktop drag still works; mobile users get a friction-free
  // alternative when drag is fiddly on a crowded canvas.
  const onCanvasClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;
      // Close any open bubble on empty-canvas click. Clicks INSIDE the
      // bubble or ON a tile are excluded.
      if (!target.closest(".sw-tile") && !target.closest(".sw-bubble")) {
        setBubbleTileId(null);
      }
      if (!editableTileId || !onTileMove || !wrapRef.current) return;
      // Ignore clicks that landed inside an existing tile.
      if (target.closest(".sw-tile")) return;
      const rect = wrapRef.current.getBoundingClientRect();
      const rawX = clampPct(clamp01((e.clientX - rect.left) / rect.width) * 100);
      const rawY = clampPct(clamp01((e.clientY - rect.top) / rect.height) * 100);
      // Snap to nearest non-overlapping slot near where the user tapped.
      const me = tiles.find((t) => t.id === editableTileId);
      const others = me ? tiles.filter((t) => t.id !== me.id) : tiles;
      const snapped = me
        ? findNonOverlappingPosition(rawX, rawY, me, others, aspectRatio, theme)
        : { x: rawX, y: rawY };
      onTileMove(editableTileId, snapped.x, snapped.y);
    },
    [editableTileId, onTileMove, tiles, aspectRatio, theme],
  );

  return (
    <div
      className={`sw-wrap sw-theme-${theme}${bubbleTileId ? " sw-bubble-open" : ""}`}
      ref={wrapRef}
      data-bubble-anchor-id={bubbleTileId ?? undefined}
      style={{
        aspectRatio: String(effectiveAspect),
        // Custom font cascades to tile names + neon footer via inherit.
        ...(customFont ? { ["--sw-custom-font" as string]: `"${customFont}", system-ui, sans-serif` } : {}),
        ...(customAccent ? { ["--sw-custom-accent" as string]: customAccent } : {}),
      }}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onClick={onCanvasClick}
    >
      <div className="sw-bg" aria-hidden="true" />
      {bgImageUrl ? (
        <div
          className="sw-bg-image"
          aria-hidden="true"
          style={{
            backgroundImage: `url("${bgImageUrl}")`,
            opacity: bgImageOpacity ?? 0.55,
          }}
        />
      ) : null}
      <div className="sw-grain" aria-hidden="true" />

      {ordered.map((tile, idx) => {
        const isDragging = tile.id === draggingId;
        const resolvedPos = resolved.get(tile.id);
        const rawX = isDragging && ghostPos ? ghostPos.x : (resolvedPos?.x ?? tile.xPct);
        const rawY = isDragging && ghostPos ? ghostPos.y : (resolvedPos?.y ?? tile.yPct);
        // Scale-aware safe zone: keep the tile's bbox inside a 4% frame
        // padding so big founder tiles can't spill past the wall edge
        // (especially on mobile where the canvas height is small).
        const { hx, hy } = tileHalfExtent(tile, effectiveAspect, theme);
        const FRAME_PAD = 4;
        const minX = Math.min(50, hx + FRAME_PAD);
        const maxX = Math.max(50, 100 - hx - FRAME_PAD);
        const minY = Math.min(50, hy + FRAME_PAD);
        const maxY = Math.max(50, 100 - hy - FRAME_PAD);
        const xPct = Math.min(maxX, Math.max(minX, rawX));
        const yPct = Math.min(maxY, Math.max(minY, rawY));
        const scale = sizeFor(tile.monthlyCents, theme) * scaleMult;
        const unlocks = unlocksFor(tile.monthlyCents, theme);
        const years = Math.floor((tile.anniversaryMonths ?? 0) / 12);
        const showHaloGraffiti = theme === "graffiti" && (tile.anniversaryMonths ?? 0) >= 6;
        const boosted = isBoosted(tile);

        const months = tile.anniversaryMonths ?? 0;
        const annivClass =
          months >= 24 ? "sw-anniv-24" :
          months >= 12 ? "sw-anniv-12" :
          months >= 6  ? "sw-anniv-6"  : "";

        const classes = [
          "sw-tile",
          `sw-style-${tile.style}`,
          tile.id === editableTileId ? "sw-editable" : "",
          isDragging ? "sw-dragging" : "",
          tile.blink && unlocks.canBlink ? "sw-blink" : "",
          unlocks.canSway ? "sw-sway" : "",
          unlocks.hasAura ? "sw-aura" : "",
          unlocks.isRainbow ? "sw-rainbow" : "",
          unlocks.isGilded ? "sw-gilded-fx" : "",
          /* Every tile gets the entry animation on first paint; the
             stagger via animation-delay below makes them cascade. */
          "sw-entry-all",
          showHaloGraffiti ? "sw-halo" : "",
          boosted ? "sw-boost" : "",
          annivClass,                                       // earned visual perks
          tile.fadeStartedAt ? "sw-legacy" : "",            // cancelled → desaturated
          tile.id === spotlightTileId ? "sw-spotlight" : "", // post-checkout celebration
          tile.id === bubbleTileId ? "sw-bubble-anchor" : "", // tile the open bubble points at
        ].filter(Boolean).join(" ");

        return (
          <div
            key={tile.id}
            className={classes}
            style={{
              left: `${xPct}%`,
              top: `${yPct}%`,
              transform: `translate(-50%, -50%) scale(${scale})`,
              color: tile.color,
              opacity: fadeOpacityFor(tile.fadeStartedAt),
              ["--sw-scale" as string]: String(scale),
              ["--sw-i" as string]: String(idx),
            }}
            onPointerDown={(e) => onPointerDown(e, tile)}
            onPointerUp={(e) => onTilePointerUp(e, tile)}
            data-bubble-anchor={tile.id}
          >
            {unlocks.isFounder && tile.founderNumber && theme === "atelier" ? (
              <div className="sw-tile-founder">
                {`ARCHITECT · №${tile.founderNumber}`}
              </div>
            ) : null}

            <div className="sw-tile-name">
              {tile.emoji ? (
                <span className="sw-tile-emoji" aria-hidden="true">{tile.emoji}</span>
              ) : null}
              {unlocks.canBadge && tile.badge && theme === "graffiti" ? (
                <span className="sw-tile-badge" aria-hidden="true">{BADGE_GLYPH[tile.badge]}</span>
              ) : null}
              {tile.name.toUpperCase()}
              {/* Atelier anniversary dots — discreet status signal, no animation. */}
              {theme === "atelier" && years > 0 ? (
                <span className="sw-tile-anniv" aria-label={`${years} year supporter`}>
                  {" "}{"●".repeat(Math.min(5, years))}
                </span>
              ) : null}
            </div>
            <div className="sw-tile-msg">{tile.message}</div>
            {tile.creatorReply ? (
              <div className="sw-tile-reply">
                <span className="sw-tile-reply-tag" aria-hidden="true">↳ creator</span>
                <span className="sw-tile-reply-text">{tile.creatorReply}</span>
              </div>
            ) : null}
            {/* Footer: DAY N · $X/mo. Per the canonical neon design source
                (5d94b180… reference image) the footer shows both the day
                count and the monthly amount, separated by a dot. AURORA-class
                founder tiles also get the inline anniversary pill. */}
            {theme !== "atelier" ? (
              <div className="sw-tile-footer">
                <span className="sw-tile-days">Day {daysForTile(tile).toLocaleString()}</span>
                {months >= 12 && unlocks.isFounder ? (
                  <span className="sw-tile-anniv-text">
                    <span aria-hidden="true">🏆</span> {Math.floor(months / 12)}-Year Golden Anniversary
                  </span>
                ) : null}
                <span className="sw-tile-dot" aria-hidden="true">·</span>
                <span className="sw-tile-amount">${(tile.monthlyCents / 100).toLocaleString()}/mo</span>
              </div>
            ) : null}

            {boosted && theme === "atelier" ? <div className="sw-tile-boosted-tag">BOOSTED</div> : null}

            {tile.id === editableTileId ? <div className="sw-tile-grip" aria-hidden="true">⤧</div> : null}
          </div>
        );
      })}

      {(() => {
        // ===== Speech bubble =====
        // Position math:
        //   - Anchor on the tile's resolved (xPct, yPct) — the same coords
        //     used to render the tile itself, so the bubble follows live.
        //   - We MEASURE the tile DOM rect to compute the real half-height
        //     in pixels (sizeFor × scaleMult makes the % approximation
        //     wildly off for founder tiles).
        //   - Flip vertically: if there's not enough headroom above the
        //     tile, render below instead.
        //   - Flip horizontally: if the tile sits in the rightmost 35% the
        //     bubble shifts its tail to the right side; same on the left.
        if (!bubbleTileId) return null;
        const tile = tiles.find((t) => t.id === bubbleTileId);
        if (!tile) return null;
        const resolvedPos = resolved.get(tile.id);
        const cx = resolvedPos?.x ?? tile.xPct;
        const cy = resolvedPos?.y ?? tile.yPct;

        // Measure the actual rendered tile so the bubble sits flush against
        // its real edge regardless of scale.
        const wrapEl = wrapRef.current;
        const wrapW = wrapEl?.clientWidth ?? 1000;
        const wrapH = wrapEl?.clientHeight ?? 600;
        const tileEl = wrapEl?.querySelector(
          `[data-bubble-anchor="${tile.id}"]`,
        ) as HTMLElement | null;
        let halfTilePx: number;
        if (tileEl && wrapEl) {
          halfTilePx = tileEl.getBoundingClientRect().height / 2;
        } else {
          const { hy } = tileHalfExtent(tile, effectiveAspect, theme);
          halfTilePx = (hy / 100) * wrapH;
        }
        // Horizontal clamp: keep the bubble fully inside the wall. The bubble
        // is centered on cx via translate(-50%), so we need at least half a
        // bubble-width of margin on each side. We shift cx → cxClamped and
        // record the pixel delta so the tail can still point at the real tile.
        const BUBBLE_W = Math.min(340, wrapW * 0.78);
        const halfBubblePx = BUBBLE_W / 2;
        const tileCenterXPx = (cx / 100) * wrapW;
        const minCenterXPx = halfBubblePx + 8;
        const maxCenterXPx = wrapW - halfBubblePx - 8;
        const clampedCenterXPx = Math.min(maxCenterXPx, Math.max(minCenterXPx, tileCenterXPx));
        const cxClamped = (clampedCenterXPx / wrapW) * 100;
        const tailShiftPx = tileCenterXPx - clampedCenterXPx; // bubble center → tile center

        // Vertical flip: enough room above? Else go below.
        const tileCenterPx = (cy / 100) * wrapH;
        const roomAbovePx = tileCenterPx - halfTilePx - 16;
        const BUBBLE_APPROX_H = 140;
        const placeBelow = roomAbovePx < BUBBLE_APPROX_H;
        // Tail horizontal position: compute as a percent of bubble width so
        // it points directly at the tile center (accounts for left/right
        // clamping above). 8%..92% so the tail never sits at the radius.
        const tailLeftPct = Math.min(92, Math.max(8,
          50 + (tailShiftPx / BUBBLE_W) * 100,
        ));

        const days = daysForTile(tile);
        const dollars = Math.round(tile.monthlyCents / 100);
        const accent = tile.color;

        const cls = [
          "sw-bubble",
          placeBelow ? "sw-bubble-below" : "sw-bubble-above",
        ].join(" ");

        return (
          <div
            className={cls}
            style={{
              left: `${cxClamped}%`,
              top: `${cy}%`,
              width: `${BUBBLE_W}px`,
              // Push the bubble off the tile by halfTilePx + 12px gap.
              ["--sw-bubble-offset" as string]: `${halfTilePx + 12}px`,
              ["--sw-bubble-accent" as string]: accent,
              ["--sw-bubble-tail-left" as string]: `${tailLeftPct}%`,
            }}
            role="dialog"
            aria-label={`${tile.name} message`}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <div className="sw-bubble-head">
              <span className="sw-bubble-name">{tile.name.toUpperCase()}</span>
              <span className="sw-bubble-meta">
                <span className="sw-bubble-amount">${dollars}</span>
                <span className="sw-bubble-dot"> · </span>
                <span className="sw-bubble-day">DAY {days}</span>
              </span>
            </div>
            <div className="sw-bubble-body">{tile.message}</div>
            <span
              className="sw-bubble-tail"
              aria-hidden="true"
              style={{ left: `var(--sw-bubble-tail-left, 50%)`, transform: "translateX(-50%)" }}
            />
          </div>
        );
      })()}
    </div>
  );
}
